<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import {
    productListStore,
    reorderProducts,
  } from '@entities/product/store.svelte';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import ProductTable from '@widgets/product-table/ProductTable.svelte';
  import ProductCreateModal from '@features/product-create/ProductCreateModal.svelte';
  import UnlinkedProductCompetitorsPanel from '@features/unlinked-product-competitor-relink/UnlinkedProductCompetitorsPanel.svelte';
  import Pagination from '@shared/ui/Pagination.svelte';
  import { navigate } from '@shared/router/navigation';
  import { canEditMasters } from '@shared/types/session';
  import type { Product } from '@domain/product/model';
  import type { ProductListQuery } from '@shared/types/product';

  let query = $state<ProductListQuery>({
    sort_by: 'sort_order',
    sort_dir: 'asc',
    page: 1,
    page_size: 50,
  });

  let createOpen = $state(false);
  let orderDirty = $state(false);
  let orderSaving = $state(false);
  let orderError = $state<string | null>(null);

  // src/pages/products.astro と同じシンプル検索 (q)。
  let q = $state('');
  let appliedQ = $state('');
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  const SEARCH_DEBOUNCE_MS = 250;

  let allowEdit = $derived(
    sessionStore.user ? canEditMasters(sessionStore.user.role) : false,
  );
  let isDefaultOrder = $derived(
    (query.sort_by ?? 'sort_order') === 'sort_order' &&
      (query.sort_dir ?? 'asc') === 'asc',
  );
  let canReorder = $derived(
    allowEdit &&
      isDefaultOrder &&
      (query.page ?? 1) === 1 &&
      appliedQ === '' &&
      productListStore.data !== null &&
      productListStore.data.total <= productListStore.data.page_size,
  );
  let showOrderToolbar = $derived(canReorder || (allowEdit && !isDefaultOrder));

  async function refresh(nextQuery: ProductListQuery = query) {
    await productListStore.refresh(nextQuery);
  }

  function applyQuery(nextQuery: ProductListQuery) {
    orderDirty = false;
    orderError = null;
    query = nextQuery;
    void refresh(nextQuery);
  }

  function clearSearchTimer() {
    if (searchTimer === null) return;
    clearTimeout(searchTimer);
    searchTimer = null;
  }

  function applyLiveSearch() {
    searchTimer = null;
    const normalized = q.trim();
    if (normalized === appliedQ) return;
    appliedQ = normalized;
    applyQuery({ ...query, q: normalized || undefined, page: 1 });
  }

  function handleSearchInput(ev: Event) {
    q = (ev.currentTarget as HTMLInputElement).value;
    clearSearchTimer();
    searchTimer = setTimeout(applyLiveSearch, SEARCH_DEBOUNCE_MS);
  }

  function toggleSort(field: NonNullable<ProductListQuery['sort_by']>) {
    let nextQuery: ProductListQuery;
    if (query.sort_by === field) {
      nextQuery = {
        ...query,
        sort_dir: query.sort_dir === 'asc' ? 'desc' : 'asc',
        page: 1,
      };
    } else {
      nextQuery = { ...query, sort_by: field, sort_dir: 'asc', page: 1 };
    }
    applyQuery(nextQuery);
  }

  function changePage(page: number) {
    applyQuery({ ...query, page });
  }

  function reorderProductIds(productIds: readonly Product['id'][]) {
    if (!canReorder) return;
    productListStore.reorderLocal(productIds);
    orderDirty = true;
    orderError = null;
  }

  async function saveOrder() {
    const data = productListStore.data;
    if (!data || !canReorder || !orderDirty) return;
    orderSaving = true;
    orderError = null;
    const productIds = data.items.map((product) => product.id);
    try {
      await reorderProducts(productIds);
      orderDirty = false;
      await refresh(query);
    } catch (err) {
      orderError = err instanceof Error ? err.message : String(err);
      orderDirty = false;
      await refresh(query);
    } finally {
      orderSaving = false;
    }
  }

  function resetOrder() {
    orderDirty = false;
    orderError = null;
    void refresh(query);
  }

  function returnToDefaultOrder() {
    applyQuery({ ...query, sort_by: 'sort_order', sort_dir: 'asc', page: 1 });
  }

  function handleCreated(product: Product) {
    createOpen = false;
    navigate(`/products/${product.id}`);
  }

  onMount(() => {
    appliedQ = q.trim();
    void refresh();
  });

  onDestroy(() => {
    clearSearchTimer();
  });
</script>

<header class="page-head">
  <div>
    <h1>自社ソリューション</h1>
  </div>
  <button
    type="button"
    class="btn btn-primary"
    disabled={!allowEdit}
    title={allowEdit ? '' : '権限がありません'}
    onclick={() => (createOpen = true)}
  >
    + 自社ソリューションを追加
  </button>
</header>

<div class="panel tools">
  <input
    type="search"
    class="search-input"
    value={q}
    oninput={handleSearchInput}
    placeholder="自社ソリューション名・概要"
    autocomplete="off"
    data-testid="product-search"
  />
</div>

<section>
  {#if productListStore.loading && productListStore.data === null}
    <div class="panel"><p class="muted">読み込み中…</p></div>
  {:else if productListStore.error}
    <div class="error">{productListStore.error}</div>
  {:else if productListStore.data}
    <div class="panel">
      {#if showOrderToolbar}
        <div class="order-toolbar">
          {#if canReorder}
            <span class="muted"
              >左端のハンドルをドラッグして表示順を変更できます</span
            >
            <button
              type="button"
              class="btn btn-primary"
              disabled={!orderDirty || orderSaving}
              onclick={saveOrder}
            >
              表示順を保存
            </button>
            {#if orderDirty}
              <button
                type="button"
                class="btn"
                disabled={orderSaving}
                onclick={resetOrder}
              >
                元に戻す
              </button>
            {/if}
          {:else if allowEdit && !isDefaultOrder}
            <button type="button" class="btn" onclick={returnToDefaultOrder}>
              表示順に戻す
            </button>
          {/if}
        </div>
      {/if}
      {#if orderError}
        <div class="error">{orderError}</div>
      {/if}
      <ProductTable
        products={productListStore.data.items}
        sortBy={query.sort_by ?? 'sort_order'}
        sortDir={query.sort_dir ?? 'asc'}
        onSort={toggleSort}
        allowReorder={canReorder}
        onReorder={reorderProductIds}
      />
      <Pagination
        total={productListStore.data.total}
        page={productListStore.data.page}
        pageSize={productListStore.data.page_size}
        onChange={changePage}
      />
    </div>
  {/if}
</section>

<UnlinkedProductCompetitorsPanel canEdit={allowEdit} />

<ProductCreateModal
  open={createOpen}
  onClose={() => (createOpen = false)}
  onCreated={handleCreated}
/>

<style>
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 12px;
    flex-wrap: wrap;
  }
  .page-head h1 {
    margin: 0;
    font-size: 1.25rem;
  }
  .page-head .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .tools {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
  }
  .tools .search-input {
    margin-left: auto;
    width: min(460px, 100%);
    min-width: min(320px, 100%);
  }
  .order-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-end;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .order-toolbar .muted {
    margin-right: auto;
    color: var(--muted, #6b7280);
    font-size: 0.875rem;
  }
</style>
